import { useCallback, useState } from "react";
import { ApiError } from "@/lib/apiClient";
import {
  acknowledgeAbsenceRequest,
  approveAbsenceRequest,
  listPendingAbsenceRequests,
  rejectAbsenceRequest,
  type EmployeeAbsenceRequest,
} from "@/lib/employeeAbsences";
import {
  listDayViolations,
  type EmployeeTimeDayApproval,
} from "@/lib/employeeTimes";
import { toast } from "@/lib/toast";
import { useRefreshKeyLoad } from "@/pages/admin/zeiterfassung/useRefreshKeyLoad";

export type DayViolationFilter = "all" | "pending" | "justified" | "historical";

export function usePendingApprovals(options?: {
  refreshKey?: number;
  onChanged?: () => void;
  enabled?: boolean;
  /** Wenn true, lädt zusätzlich die Verstoß-Übersicht (ohne Freigabe-Aktionen). */
  loadDayViolations?: boolean;
  initialDayStatusFilter?: DayViolationFilter;
}) {
  const refreshKey = options?.refreshKey ?? 0;
  const onChanged = options?.onChanged;
  const enabled = options?.enabled ?? true;
  const loadDayViolationsFlag = options?.loadDayViolations ?? true;
  const initialFilter = options?.initialDayStatusFilter ?? "all";

  const [dayItems, setDayItems] = useState<EmployeeTimeDayApproval[]>([]);
  const [absenceItems, setAbsenceItems] = useState<EmployeeAbsenceRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyAbsenceId, setBusyAbsenceId] = useState<number | null>(null);
  const [absenceComments, setAbsenceComments] = useState<Record<number, string>>({});

  const [dayStatusFilter, setDayStatusFilter] = useState<DayViolationFilter>(initialFilter);
  const [dayUserFilter, setDayUserFilter] = useState("");
  const [dayDateFrom, setDayDateFrom] = useState("");
  const [dayDateTo, setDayDateTo] = useState("");

  const [appliedFilters, setAppliedFilters] = useState({
    status: initialFilter as DayViolationFilter,
    user_login: "",
    date_from: "",
    date_to: "",
  });

  const load = useCallback(
    async (opts?: { silent?: boolean }) => {
      if (!enabled) {
        setDayItems([]);
        setAbsenceItems([]);
        setLoading(false);
        return;
      }
      if (!opts?.silent) setLoading(true);
      try {
        const absencesPromise = listPendingAbsenceRequests();
        const daysPromise = loadDayViolationsFlag
          ? listDayViolations({
              status: appliedFilters.status,
              user_login: appliedFilters.user_login.trim() || undefined,
              date_from: appliedFilters.date_from || undefined,
              date_to: appliedFilters.date_to || undefined,
            })
          : Promise.resolve([] as EmployeeTimeDayApproval[]);
        const [days, absences] = await Promise.all([daysPromise, absencesPromise]);
        setDayItems(days);
        setAbsenceItems(absences);
      } catch (err) {
        toast.error(
          err instanceof ApiError ? err.message : "Freigaben konnten nicht geladen werden.",
        );
      } finally {
        setLoading(false);
      }
    },
    [enabled, loadDayViolationsFlag, appliedFilters],
  );

  useRefreshKeyLoad(load, refreshKey);

  const setAbsenceComment = useCallback((id: number, value: string) => {
    setAbsenceComments((prev) => ({ ...prev, [id]: value }));
  }, []);

  const reviewAbsence = useCallback(
    async (id: number, action: "approve" | "reject") => {
      setBusyAbsenceId(id);
      try {
        const comment = absenceComments[id]?.trim() || undefined;
        if (action === "approve") {
          await approveAbsenceRequest(id, comment);
        } else {
          await rejectAbsenceRequest(id, comment);
        }
        onChanged?.();
        await load({ silent: true });
      } catch (err) {
        toast.error(err instanceof ApiError ? err.message : "Aktion fehlgeschlagen.");
      } finally {
        setBusyAbsenceId(null);
      }
    },
    [absenceComments, load, onChanged],
  );

  const acknowledgeAbsence = useCallback(
    async (id: number) => {
      setBusyAbsenceId(id);
      try {
        await acknowledgeAbsenceRequest(id);
        toast.success("Als gelesen markiert. Der Zeitraum bleibt im Montageplan.");
        onChanged?.();
        await load({ silent: true });
      } catch (err) {
        toast.error(err instanceof ApiError ? err.message : "Aktion fehlgeschlagen.");
      } finally {
        setBusyAbsenceId(null);
      }
    },
    [load, onChanged],
  );

  const applyDayFilters = useCallback(() => {
    setAppliedFilters({
      status: dayStatusFilter,
      user_login: dayUserFilter,
      date_from: dayDateFrom,
      date_to: dayDateTo,
    });
  }, [dayStatusFilter, dayUserFilter, dayDateFrom, dayDateTo]);

  const pendingJustificationCount = dayItems.filter(
    (item) => item.requires_justification || (!item.has_justification && item.status === "pending"),
  ).length;

  return {
    dayItems,
    absenceItems,
    loading,
    busyAbsenceId,
    absenceComments,
    setAbsenceComment,
    reviewAbsence,
    acknowledgeAbsence,
    reload: () => void load({ silent: true }),
    totalPending: absenceItems.length,
    pendingJustificationCount,
    dayStatusFilter,
    setDayStatusFilter,
    dayUserFilter,
    setDayUserFilter,
    dayDateFrom,
    setDayDateFrom,
    dayDateTo,
    setDayDateTo,
    applyDayFilters,
  };
}
